import React, { useCallback, useEffect } from 'react';
import ConfigureTemplatizedAPISinkContainer from 'js/components/destination/destination-content/ConfigureTemplatizedAPISinkContainer';
import {
  CONNECTION_CATEGORY_REST,
  CONNECTION_CATEGORY_TEMPLATIZED_API,
} from 'js/constants/connectionCategories';
import useApp from 'new/state/app/repository/useApp';
import usePaging from 'new/state/paging/repository/usePaging';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import useSchemas from 'new/state/dataSetSchemas/useSchemas';
import useFlowsSetup from 'new/components/flows/controller/useFlowsSetup';
import useFlows from 'new/components/flows/useFlows';
import useCredentials from 'new/state/credentials/repository/useCredentials';
import useCredentialsAdapter from 'new/state/credentials/adapter/useCredentialsAdapter';
import useVendorsAdapter from 'new/state/vendors/adapter/useVendorsAdapter';
import { find, get, isEmpty, isInteger, reduce } from 'lodash';
import { useSamples } from 'new/state/samples/repository/useSamples';
import UIUtils from 'js/lib/UIUtils';
import createFlowService from 'new/components/flows/service/createFlowService';
import { getEmptyRequiredFields } from 'js/lib/ValidationUtils';
import useRequests from 'new/state/request/repository/useRequests';
import { getActiveItems } from 'new/components/flows/types/DirectFlow/utils';
import { tryCatch } from 'new/utils/TryCatch';
import { useDispatch } from 'react-redux';
import { addCredentialFromForm, updateCredentialFromForm } from 'js/actions';
import { getConnectionOrVendorType } from 'js/lib/ResourceUtils';
import { useVendors } from 'new/state/vendors/repository/useVendors';
import { CenteredLoader } from 'new/elements/CenteredLoader';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { useHistory } from 'react-router-dom';
import useFlowParams from '../../useFlowParams';
import DropboxService from 'js/services/DropboxService';
import BoxService from 'js/services/BoxService';

type OAuthCredentialCallback = (authInfo: { id: number } | Record<string, unknown>) => void;

type Props = {
  formType: string;
  flowType: string;
  sink: any;
  nexset: any;
  sinkConfig: any;
  isEditMode: boolean;
  onSaveAdvancedMode(data: any): void;
  nextFormStage(...args: any): void;
  toggleDestinationDraft(value: boolean): void;
};

const service = createFlowService();

export function RestTemplatizedDestination(props: Props) {
  const { formType, flowType, sink, nexset, sinkConfig, isEditMode } = props;

  const ctx = useApp();
  const history = useHistory();
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const { samples } = useSamples();
  const { showError } = useRequests();
  const organization = useCurrentOrg();
  const { credentials } = useCredentials();
  const { getNexsetSchemaById } = useSchemas();
  const { vendors, vendorEndpoints } = useVendors();
  const { credentialsById, credentialsByType } = useCredentials();
  const { paging, getSinkConfigFields, getMetaInfoById, getAuthFields } = usePaging();
  /**
   * @Adapters
   */
  const { loadCredentials } = useCredentialsAdapter();
  const { requestFullVendor } = useVendorsAdapter();

  const { data: formData, updateFormData, onSinkConfigChanged } = useFlowsSetup(formType, flowType);
  const { isModal, onDismiss } = useFlowParams();
  const { onSinkMappingUpdated } = useFlows(updateFormData);

  const goBack = useCallback(() => {
    if (isModal) {
      onDismiss?.({});
      return;
    }
    history?.goBack();
  }, [isModal, onDismiss, history]);

  const sinkConnectionOrVendorType = sink?.sink_type
    ? getConnectionOrVendorType(sink, vendors, 'sink_type', 'vendor_id')
    : formData.selectedConnection;

  useEffect(() => {
    if (nexset?.id && !formData?.toNested) {
      const conn = getAuthFields(sinkConnectionOrVendorType);
      const { templates = [], items = [] } = conn ?? {};

      updateFormData({
        /**
         * Set the nested samples to generate the previewSamples array
         */
        toNested: get(samples, `[${nexset?.id}].original.out`),
        connectionAuthFields: getActiveItems(templates, items, undefined),
      });
    }
  }, [samples, formData?.toNested]);

  const { selectedCredential, selectedConnection: connection } = formData;

  const isLoadingCredentials = false;
  const { allowSinkAutoMapping } = getMetaInfoById(connection);
  const sinkConfigFields = getSinkConfigFields(connection, flowType);
  const nexsetSchema = getNexsetSchemaById(nexset?.id);
  const connectionAuth = getAuthFields(sinkConnectionOrVendorType);

  const onChangeApiType = (vendorName: string) => requestFullVendor(vendorName);

  const handleNewDropboxCredential = useCallback(
    (callback: OAuthCredentialCallback) => {
      DropboxService.authenticate(callback, formData.selectedCredential);
    },
    [formData.selectedCredential],
  );

  const handleNewBoxCredential = useCallback(
    (callback: OAuthCredentialCallback) => {
      BoxService.authenticate(callback, formData.selectedCredential);
    },
    [formData.selectedCredential],
  );

  const getDefaultSinkConfig = (buildNew = false, selectedConnection = connection) => {
    const { error: errorDefaultSinkConfig, data: resultDefaultSinkConfig } = tryCatch(() =>
      service.createDefaultSinkConfig({
        sinkConfig,
        isEditMode,
        selectedConnection,
        allowSinkAutoMapping,
        buildNew,
        orgId: organization.id,
        dataSchema: nexsetSchema,
        configFields: sinkConfigFields,
        connectionCategory: CONNECTION_CATEGORY_TEMPLATIZED_API,
      }),
    );

    if (errorDefaultSinkConfig) {
      console.warn(errorDefaultSinkConfig.message);
      return null;
    }

    return resultDefaultSinkConfig;
  };

  const handleSinkConfigChanged = (newSinkConfig = {}, field = null, value = null) => {
    let sinkConfig = UIUtils.fillConditionalDefault(newSinkConfig, sinkConfigFields, field, value);

    service.setHCInSinkConfig(sinkConfig, {
      orgId: organization.id,
      sinkConfigFields,
      selectedConnection: connection,
    });

    updateFormData({ sinkConfig });
  };

  const handleTemplatizedAPIConfigChanged = (name: string, newValue: any) => {
    const value = 'parameters.to_email' === name ? newValue.trim() : newValue;
    handleSinkConfigChanged({ ...sinkConfig, [name]: value }, name, value);
  };

  const handleScriptCredentialInputChanged = (name: string, input: any) => {
    const { scriptCredentialsData } = formData;
    const thisCredentialData = scriptCredentialsData[name] || {};
    const updatedCredentialData = { ...thisCredentialData, ...input };
    const newData = { ...scriptCredentialsData, [name]: updatedCredentialData };

    if (isInteger(updatedCredentialData?.selectedCredentialId)) {
      handleTemplatizedAPIConfigChanged(name, updatedCredentialData.selectedCredentialId);
    }

    updateFormData({ scriptCredentialsData: newData });
  };

  const handleTemplatizedAPIModeChanged = (isAdvancedMode: boolean, newConfig: any) => {
    const sinkMapping = Object.create(formData?.sinkMapping);
    const currentSinkConfig = Object.create(formData.sinkConfig);

    const { vendor_endpoint_id } = currentSinkConfig;

    if (isAdvancedMode) {
      const { vendorId } = paging.metaInfoById[sinkConnectionOrVendorType];
      let sinkConfig = getDefaultSinkConfig(true, CONNECTION_CATEGORY_REST);
      sinkConfig = service.getSinkConfigWithMapping({
        sinkConfig,
        isEditMode,
        sinkMapping,
        connectionCategory: CONNECTION_CATEGORY_REST,
      });

      sinkConfig.vendor_id = vendorId;
      sinkConfig = { ...sinkConfig, ...newConfig };
      updateFormData({
        sinkConfig: { ...currentSinkConfig, ...sinkConfig, sink_type: 'rest' },
      });
    } else {
      delete currentSinkConfig.vendor_id;
      updateFormData({
        sinkConfig: {
          ...currentSinkConfig,
          ...getDefaultSinkConfig(true),
          vendor_endpoint_id,
          advanced_templatized_api: false,
        },
      });
    }
  };

  const handleSaveNewScriptCredential = (name: string | number) => {
    const { templatizedScriptConfig, credentialCreationStatus } = formData;

    const { sinkConfigById } = paging;
    const { scriptCredentialsData } = formData;
    const thisCredentialData = formData.scriptCredentialsData[name] || {};
    const { selectedCredentialInfo } = thisCredentialData;

    // required field validation
    const { credential_type } = find(sinkConfigById[connection], {
      name,
    });
    const { templates, items } = connectionAuth;
    const connectionAuthFields = getActiveItems(templates, items, undefined);

    const missingFields = getEmptyRequiredFields(connectionAuthFields, selectedCredential);

    /**
     * check if credential name has been added or not
     */
    if (!get(selectedCredentialInfo, 'cred_name')) {
      missingFields.unshift({
        name: 'cred_name',
        display_name: 'Credential Name',
      });
    }

    if (!isEmpty(missingFields)) {
      let error = reduce(
        missingFields,
        (message, field) => `${message} ${field.display_name},`,
        'Please enter the following required fields: ',
      );
      error = error.substring(0, error.length - 1);
      showError(error);
      return;
    }

    // if cred created, select it
    const updatedCredentialData = {
      ...thisCredentialData,
      isNewCredential: false,
      selectedCredentialId: credentialCreationStatus.credential,
    };

    const newData = {
      ...scriptCredentialsData,
      [name]: updatedCredentialData,
    };
    const newTemplatizedScriptConfig = {
      ...templatizedScriptConfig,
      [name]: updatedCredentialData?.selectedCredentialId,
    };

    updateFormData({
      connectionAuthFields,
      scriptCredentialsData: newData,
      credentialCreationStatus: null,
      templatizedScriptConfig: newTemplatizedScriptConfig,
    });

    // request cred creation
    const credentialId = credentialCreationStatus ? credentialCreationStatus.credential : null;

    const data = {
      ...selectedCredentialInfo,
      _vendorConnectionType: credential_type,
    };
    handleRequestCredentialCreation(data, credentialId);
  };

  const handleRequestCredentialCreation = (credentialInfo: any, credentialId: number) => {
    let newSinkConfig = getDefaultSinkConfig();

    updateFormData({
      sinkConfig: newSinkConfig,
      addedDataSinkId: '',
      selectedCredential: '',
      credentialProbeStatus: '',
      credentialCreationStatus: '',
    });

    if (credentialId) {
      dispatch(updateCredentialFromForm(formType, credentialId, credentialInfo));
    } else {
      const connectionType = credentialInfo?._vendorConnectionType || connection;
      dispatch(addCredentialFromForm(formType, connectionType, credentialInfo));
    }
  };

  if (!connection) {
    return <CenteredLoader />;
  }

  return (
    <ConfigureTemplatizedAPISinkContainer
      formType={formType}
      paging={paging}
      dataSet={nexset}
      dataSetId={nexset?.id}
      addedDataSink={sink}
      currentOrg={organization}
      sinkConfig={sinkConfig}
      credentials={credentials}
      templatizedAPIConfig={sinkConfig}
      isEditMode={isEditMode}
      dataSchema={nexsetSchema}
      vendors={vendors}
      samples={formData?.toNested}
      vendorEndpoints={vendorEndpoints}
      credentialsById={credentialsById}
      credentialsByType={credentialsByType}
      isLoadingCredentials={isLoadingCredentials}
      onLoadCredentials={loadCredentials}
      selectedCredential={selectedCredential}
      sinkConfigById={paging.sinkConfigById}
      selectedCredentialId={selectedCredential?.id}
      credentialsData={formData?.scriptCredentialsData}
      connectionAuthFields={formData?.connectionAuthFields}
      selectedConnection={sink?.id ? sinkConnectionOrVendorType : connection}
      // actions
      onChangeApiType={onChangeApiType}
      onNewDropboxCredential={handleNewDropboxCredential}
      onNewBoxCredential={handleNewBoxCredential}
      onSinkConfigChanged={onSinkConfigChanged}
      onValidateNewScriptCredential={handleSaveNewScriptCredential}
      onTemplatizedAPIModeChanged={handleTemplatizedAPIModeChanged}
      onTemplatizedAPIConfigChanged={handleTemplatizedAPIConfigChanged}
      onScriptCredentialInputChanged={handleScriptCredentialInputChanged}
      onSinkMappingUpdated={(newSinkConfig: any, auto: boolean) => {
        if (newSinkConfig) {
          onSinkMappingUpdated({
            isEditMode,
            sinkConfig,
            connectionCategory: CONNECTION_CATEGORY_TEMPLATIZED_API,
          })(newSinkConfig, auto);
        }
      }}
      onToggleDestinationDraft={props.toggleDestinationDraft}
      setContextualSupport={ctx.setContextualSupport}
      unsetContextualSupport={ctx.unsetContextualSupport}
      onSaveAdvancedMode={props?.onSaveAdvancedMode}
      nextFormStage={props?.nextFormStage}
      goBack={goBack}
      onError={showError}
    />
  );
}
